import { Box } from '@mantine/core'
import { useRef, useState } from 'react'
import { useIntl } from 'react-intl'
import { extractErrorMessage } from '@/api/helpers/extractErrorMessage'
import { LoadingOverlay } from '@/components/ui-shared/LoadingOverlay/LoadingOverlay'
import {
  useCreateDeploymentAlertSetting,
  useDeleteDeploymentAlertSetting,
  useInvalidateDeploymentAlertSettings,
  useUpdateDeploymentAlertSetting
} from '@/queries/deploymentAlertQueries'
import { showToast } from '@/theme/notifications'
import { DeploymentAlertSetting } from '@/types/deploymentAlert'
import { Device } from '@/types/device'
import { DeleteNotificationRuleModal } from './DeleteNotificationRuleModal'
import { NotificationRulesForm } from './NotificationRulesForm'
import {
  CreatedRuleAssignment,
  NotificationsFormValues,
  SavedNotificationRuleFormValues,
  buildRuleChanges,
  idsFromSettledCreates,
  toRulePayload
} from './notificationRules'

type NotificationRulesHandlerProps = {
  alertSettings: DeploymentAlertSetting[]
  devices: Device[]
  isLoadingDevices?: boolean
  canManage?: boolean
}

export const NotificationRulesHandler = ({
  alertSettings,
  devices,
  isLoadingDevices,
  canManage
}: NotificationRulesHandlerProps) => {
  const intl = useIntl()

  const [ruleToDelete, setRuleToDelete] =
    useState<SavedNotificationRuleFormValues | null>(null)

  const { mutateAsync: createAlertSetting } = useCreateDeploymentAlertSetting()
  const { mutateAsync: updateAlertSetting } = useUpdateDeploymentAlertSetting()
  const { mutateAsync: deleteAlertSetting } = useDeleteDeploymentAlertSetting()

  const { invalidateDeploymentAlertSettings } =
    useInvalidateDeploymentAlertSettings()

  // Tracked here rather than read off the mutations: one save fans out into
  // several of them, and the form must stay locked through the refetch that
  // follows, not just until the last request resolves.
  const [isSaving, setIsSaving] = useState(false)

  // Remount only after a full Save so the form reseeds from the server.
  // A partial save assigns IDs onto successful creates instead — remounting
  // would drop failed drafts and in-progress edits. Do not key this on the
  // query's dataUpdatedAt — a modal delete invalidates that query and would
  // wipe in-progress edits to other rules.
  const [formKey, setFormKey] = useState(0)
  const [removedIds, setRemovedIds] = useState<string[]>([])
  const removeFromFormRef = useRef<(() => void) | null>(null)

  const baseline = alertSettings.filter(
    (setting) => !removedIds.includes(setting.id)
  )

  const getDeviceNames = (rule: SavedNotificationRuleFormValues | null) =>
    (rule?.device_ids || []).map(
      (deviceId) =>
        devices.find((device) => device.id === deviceId)?.label || deviceId
    )

  const handleSave = async (
    values: NotificationsFormValues,
    assignCreatedIds: (assignments: CreatedRuleAssignment[]) => void
  ) => {
    const { created, updated, deletedIds } = buildRuleChanges(
      baseline,
      values.rules
    )

    setIsSaving(true)
    let saved = false
    let createdIds: CreatedRuleAssignment[] = []

    try {
      // allSettled so the refetch in `finally` cannot run while sibling requests
      // are still in flight and capture a half-applied rule set.
      const results = await Promise.allSettled([
        ...deletedIds.map((id) => deleteAlertSetting(id)),
        ...updated.map((rule) =>
          updateAlertSetting({ id: rule.id, data: toRulePayload(rule) })
        ),
        ...created.map((rule) => createAlertSetting(toRulePayload(rule)))
      ])

      const createResults = results.slice(deletedIds.length + updated.length)

      createdIds = idsFromSettledCreates(
        created,
        createResults as PromiseSettledResult<DeploymentAlertSetting>[]
      )

      const failure = results.find(
        (result): result is PromiseRejectedResult =>
          result.status === 'rejected'
      )

      if (failure) {
        throw failure.reason
      }

      saved = true

      showToast(
        intl.formatMessage({ id: 'workspace.notifications.save.success' }),
        'success'
      )
    } catch (err) {
      showToast(
        extractErrorMessage(
          err,
          intl.formatMessage({ id: 'workspace.notifications.save.error' })
        ),
        'error'
      )
    } finally {
      assignCreatedIds(createdIds)

      // Requests run in parallel, so a rejection still leaves the earlier ones
      // applied — refetch either way so the form reflects what actually persisted.
      await invalidateDeploymentAlertSettings()

      if (saved) {
        setRemovedIds([])
        setFormKey((key) => key + 1)
      }

      setIsSaving(false)
    }
  }

  return (
    <Box pos="relative">
      <LoadingOverlay visible={isSaving} />

      <NotificationRulesForm
        key={formKey}
        alertSettings={baseline}
        devices={devices}
        isLoadingDevices={isLoadingDevices}
        isSaving={isSaving}
        canManage={canManage}
        onDeleteSavedRule={(rule, removeFromForm) => {
          removeFromFormRef.current = removeFromForm
          setRuleToDelete(rule)
        }}
        onSubmit={(values, assignCreatedIds) =>
          void handleSave(values, assignCreatedIds)
        }
      />

      <DeleteNotificationRuleModal
        opened={!!ruleToDelete}
        ruleId={ruleToDelete?.id}
        deviceNames={getDeviceNames(ruleToDelete)}
        onClose={() => {
          removeFromFormRef.current = null
          setRuleToDelete(null)
        }}
        onDeleted={() => {
          if (ruleToDelete) {
            setRemovedIds((ids) => [...ids, ruleToDelete.id])
          }

          removeFromFormRef.current?.()
          removeFromFormRef.current = null
          setRuleToDelete(null)
          void invalidateDeploymentAlertSettings()
        }}
      />
    </Box>
  )
}
